import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Shield } from 'lucide-react';

export default function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already accepted cookies
    const cookiesAccepted = localStorage.getItem('cookies_accepted');
    if (!cookiesAccepted) {
      // Small delay to not show immediately on page load
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('cookies_accepted', 'true');
    localStorage.setItem('cookies_accepted_date', new Date().toISOString());
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6"
          data-testid="cookie-banner"
        >
          <div className="max-w-4xl mx-auto">
            <div className="glass-card p-4 sm:p-6 shadow-2xl" style={{ borderColor: 'rgba(230,195,106,0.12)' }}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                {/* Icon */}
                <div className="hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-purple-400/10 flex-shrink-0">
                  <Cookie className="w-6 h-6 text-gold-300" />
                </div>
                
                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Cookie className="w-5 h-5 text-gold-300 sm:hidden" />
                    <h3 className="font-semibold text-white text-sm sm:text-base">Uso de Cookies</h3>
                  </div>
                  <p className="text-[var(--text-muted)] text-xs sm:text-sm leading-relaxed">
                    Utilizamos cookies <strong className="text-[var(--text-secondary)]">esenciales</strong> para mantener tu sesion y mejorar tu experiencia. 
                    No usamos cookies de seguimiento ni publicidad.{' '}
                    <Link to="/privacy" className="text-gold-300 hover:text-gold-200 underline underline-offset-2">
                      Leer mas
                    </Link>
                  </p>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button
                    onClick={acceptCookies}
                    className="flex-1 sm:flex-none px-5 py-2.5 bg-purple-400 hover:bg-purple-500 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                    data-testid="cookie-accept-btn"
                  >
                    <Shield className="w-4 h-4" />
                    Aceptar
                  </button>
                  <button
                    onClick={acceptCookies}
                    className="p-2 text-[var(--text-muted)] hover:text-white hover:bg-white/10 rounded-lg transition-colors sm:hidden"
                    aria-label="Cerrar"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {/* Additional info - desktop only */}
              <div className="hidden sm:flex items-center gap-4 mt-4 pt-4 border-t border-white/5 text-xs text-[var(--text-muted)]">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span>Cookies de sesion</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span>Almacenamiento local</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                  <span>Sin rastreo</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                  <span>Sin publicidad</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
